/**
 * Initialize the before-after section with twentytwenty slider
 * Adapted from the original twentytwenty-init.js for React/TypeScript
 */

declare global {
    interface Window {
        jQuery: any;
        $: any;
    }
}

/**
 * Destroy existing twentytwenty instance
 */
function destroyTwentyTwenty(twentyTwentyElement: HTMLDivElement): void {
    if (!twentyTwentyElement.classList.contains('twentytwenty-container')) {
        return; // Not initialized, nothing to destroy
    }

    // Find and remove the twentytwenty-wrapper
    const wrapper = twentyTwentyElement.closest('.twentytwenty-wrapper');
    if (wrapper && wrapper.parentElement) {
        // Get the original images
        const images = twentyTwentyElement.querySelectorAll('img');
        
        // Create a new container with the original structure
        const newContainer = document.createElement('div');
        newContainer.id = twentyTwentyElement.id;
        newContainer.className = twentyTwentyElement.className.replace('twentytwenty-container', '').trim();
        
        // Re-add the images
        images.forEach(img => {
            newContainer.appendChild(img.cloneNode(true));
        });
        
        // Replace the wrapper with the new clean container
        wrapper.parentElement.replaceChild(newContainer, wrapper);
    }
}

/**
 * Initialize twentytwenty after images are loaded
 */
export function initializeBeforeAfterSection(
    _container: HTMLElement,
    twentyTwentyElement: HTMLDivElement,
    beforeLabel: string = 'Before',
    afterLabel: string = 'After'
): void {
    const containerId = twentyTwentyElement.id;
    if (!containerId) {
        return;
    }

    // Check if already initialized - if so, update labels
    if (twentyTwentyElement.classList.contains('twentytwenty-container')) {
        // Find the wrapper which contains the labels
        const wrapper = twentyTwentyElement.closest('.twentytwenty-wrapper');
        
        if (wrapper) {
            const beforeLabelEl = wrapper.querySelector('.twentytwenty-before-label');
            const afterLabelEl = wrapper.querySelector('.twentytwenty-after-label');
            
            if (beforeLabelEl && afterLabelEl) {
                // Update the data-content attributes which control the label text
                beforeLabelEl.setAttribute('data-content', beforeLabel);
                afterLabelEl.setAttribute('data-content', afterLabel);
                
                // Force a reflow to ensure the changes are applied
                (wrapper as HTMLElement).offsetHeight; // Trigger reflow
                return;
            }
        }
        
        // If we can't find or update labels, destroy and reinitialize
        destroyTwentyTwenty(twentyTwentyElement);
        
        // Get the new element after destruction
        twentyTwentyElement = document.getElementById(containerId) as HTMLDivElement;
        if (!twentyTwentyElement) {
        return;
        }
    }

    // Ensure container is visible before initialization
    twentyTwentyElement.style.visibility = 'visible';
    twentyTwentyElement.style.opacity = '1';
    twentyTwentyElement.style.display = 'block';

    const images = twentyTwentyElement.querySelectorAll('img');
    if (images.length < 2) {
        return;
    }

    const beforeImg = images[0] as HTMLImageElement;
    const afterImg = images[1] as HTMLImageElement;

    // Ensure images are visible
    beforeImg.style.visibility = 'visible';
    beforeImg.style.opacity = '1';
    afterImg.style.visibility = 'visible';
    afterImg.style.opacity = '1';

    // Function to check if images are loaded
    function checkImagesLoaded(): boolean {
        return (
            beforeImg.complete &&
            afterImg.complete &&
            beforeImg.naturalWidth > 0 &&
            beforeImg.naturalHeight > 0 &&
            afterImg.naturalWidth > 0 &&
            afterImg.naturalHeight > 0
        );
    }

    // Function to ensure element visibility
    function ensureElementVisible(element: HTMLElement | null): void {
        if (!element) return;
        element.style.visibility = 'visible';
        element.style.opacity = '1';
        element.style.display = 'block';
    }

    // Function to initialize twentytwenty
    function doInitialize(): boolean {
        // Check if jQuery and twentytwenty are available
        if (typeof window.jQuery === 'undefined' || !window.jQuery.fn.twentytwenty) {
            return false;
        }

        // Ensure container and images are visible before initialization
        ensureElementVisible(twentyTwentyElement);
        ensureElementVisible(beforeImg);
        ensureElementVisible(afterImg);

        try {
            // Force visibility before initialization
            twentyTwentyElement.style.visibility = 'visible';
            twentyTwentyElement.style.opacity = '1';
            twentyTwentyElement.style.display = 'block';

            // Initialize twentytwenty using jQuery with responsive options
            window.jQuery('#' + containerId).twentytwenty({
                before_label: beforeLabel,
                after_label: afterLabel,
                move_slider_on_hover: false,
                click_to_move: true,
                move_with_handle_only: false,
            });

            // Ensure visibility after initialization
            setTimeout(() => {
                const initializedContainer = document.getElementById(containerId);
                if (initializedContainer) {
                    ensureElementVisible(initializedContainer);
                    const wrapper = initializedContainer.closest('.twentytwenty-wrapper') as HTMLElement;
                    if (wrapper) {
                        ensureElementVisible(wrapper);
                    }
                }
            }, 100);

            return true;
        } catch (error) {
            // Try again after a short delay
            setTimeout(() => {
                if (!twentyTwentyElement.classList.contains('twentytwenty-container')) {
                    ensureElementVisible(twentyTwentyElement);
                    doInitialize();
                }
            }, 500);
            return false;
        }
    }

    // Check if images are already loaded
    if (checkImagesLoaded()) {
        // Images already loaded, initialize after ensuring visibility
        setTimeout(() => {
            twentyTwentyElement.style.visibility = 'visible';
            twentyTwentyElement.style.opacity = '1';
            doInitialize();
        }, 150);
    } else {
        // Wait for images to load
        let imagesLoaded = 0;
        const totalImages = 2;
        let initializationAttempted = false;

        function onImageLoad(): void {
            imagesLoaded++;
            if (imagesLoaded >= totalImages && checkImagesLoaded() && !initializationAttempted) {
                initializationAttempted = true;
                setTimeout(() => {
                    twentyTwentyElement.style.visibility = 'visible';
                    twentyTwentyElement.style.opacity = '1';
                    doInitialize();
                }, 150);
            }
        }

        function onImageError(_imageType: string): void {
            imagesLoaded++;
            if (imagesLoaded >= totalImages && !initializationAttempted) {
                initializationAttempted = true;
                setTimeout(() => {
                    twentyTwentyElement.style.visibility = 'visible';
                    twentyTwentyElement.style.opacity = '1';
                    doInitialize();
                }, 150);
            }
        }

        // Add load listeners
        if (!beforeImg.complete) {
            beforeImg.addEventListener('load', onImageLoad, { once: true });
            beforeImg.addEventListener('error', () => onImageError('Before'), { once: true });
        } else {
            imagesLoaded++;
        }

        if (!afterImg.complete) {
            afterImg.addEventListener('load', onImageLoad, { once: true });
            afterImg.addEventListener('error', () => onImageError('After'), { once: true });
        } else {
            imagesLoaded++;
        }

        // If both images are already loaded
        if (imagesLoaded >= totalImages && checkImagesLoaded() && !initializationAttempted) {
            initializationAttempted = true;
            setTimeout(() => {
                twentyTwentyElement.style.visibility = 'visible';
                twentyTwentyElement.style.opacity = '1';
                doInitialize();
            }, 150);
        }

        // Fallback: Initialize after timeout even if images seem not loaded
        setTimeout(() => {
            if (!twentyTwentyElement.classList.contains('twentytwenty-container') && !initializationAttempted) {
                initializationAttempted = true;
                twentyTwentyElement.style.visibility = 'visible';
                twentyTwentyElement.style.opacity = '1';
                doInitialize();
            }
        }, 3000);

        // Also try on window load
        if (document.readyState === 'loading') {
            window.addEventListener(
                'load',
                () => {
                    setTimeout(() => {
                        if (
                            !twentyTwentyElement.classList.contains('twentytwenty-container') &&
                            !initializationAttempted
                        ) {
                            initializationAttempted = true;
                            twentyTwentyElement.style.visibility = 'visible';
                            twentyTwentyElement.style.opacity = '1';
                            doInitialize();
                        }
                    }, 500);
                },
                { once: true }
            );
        } else {
            // Window already loaded
            setTimeout(() => {
                if (
                    !twentyTwentyElement.classList.contains('twentytwenty-container') &&
                    checkImagesLoaded() &&
                    !initializationAttempted
                ) {
                    initializationAttempted = true;
                    twentyTwentyElement.style.visibility = 'visible';
                    twentyTwentyElement.style.opacity = '1';
                    doInitialize();
                }
            }, 500);
        }
    }

    // Setup intersection observer to initialize when element becomes visible
    if ('IntersectionObserver' in window) {
        const visibilityObserver = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        const observedContainer = entry.target as HTMLElement;
                        if (!observedContainer.classList.contains('twentytwenty-container')) {
                            setTimeout(() => {
                                observedContainer.style.visibility = 'visible';
                                observedContainer.style.opacity = '1';
                                doInitialize();
                            }, 200);
                        } else {
                            observedContainer.style.visibility = 'visible';
                            observedContainer.style.opacity = '1';
                        }
                    }
                });
            },
            {
                threshold: 0.1,
                rootMargin: '50px',
            }
        );

        visibilityObserver.observe(twentyTwentyElement);
    }

    // Force visibility check on resize
    const resizeHandler = (): void => {
        if (twentyTwentyElement.classList.contains('twentytwenty-container')) {
            twentyTwentyElement.style.visibility = 'visible';
            twentyTwentyElement.style.opacity = '1';
            twentyTwentyElement.style.display = 'block';
        }
    };

    window.addEventListener('resize', resizeHandler);

    // Cleanup function (can be called from React component cleanup)
    window.removeEventListener('resize', resizeHandler);
}

